import { getProfile, getProjects, storeSession } from "helpers/apiClient";
import { loadCurrentUserParcels } from "helpers/projectUsers";
import { widgetData } from "data/chartData/frontLayout";
import { frontLayouts } from "data/frontLayouts";
import { getRolePermissions } from "data/rolePermissions";

const canonicalRoles = new Set(["admin", "tecnico", "regante"]);
const isApproved = (status) => String(status || "").toLowerCase() === "approved";

const selectCurrentProject = (response) => {
  const projects = Array.isArray(response) ? response : response?.projects || [];
  return projects.find((project) => project.isDefault) || projects[0] || null;
};

const normalizeParcels = (parcels) =>
  (Array.isArray(parcels) ? parcels : []).map((parcel) => ({
    id: parcel.id ?? parcel.parcelId,
    irrigationReference: parcel.irrigationReference ?? null,
    cadastralReference: parcel.cadastralReference ?? null,
    polygonReference: parcel.polygonReference ?? null,
    irrigationOrder: parcel.irrigationOrder ?? null,
  }));

const parcelProfileFields = (parcels) => {
  const [parcel = {}] = parcels;
  return {
    parcels,
    // Legacy Profile fields remain a compatibility projection only. They are
    // always replaced from the authoritative assignments, never local storage.
    parcela: parcel.irrigationReference ?? "",
    catastro: parcel.cadastralReference ?? "",
    poligono: parcel.polygonReference ?? "",
    parcela_orden: parcel.irrigationOrder ?? "",
  };
};

export const mergeProfileResponse = (data, previous, assignedParcels = []) => {
  const parcels = normalizeParcels(assignedParcels);
  return {
    ...previous,
	userId: data.userId ?? previous.userId ?? null,
	...parcelProfileFields(parcels),
	username: data.username ?? previous.username ?? "",
    isSuperadmin: Boolean(data.isSuperadmin),
    isVerified: Boolean(data.validated),
    isEmailVerified: Boolean(data.validated),
    phone: data.phone || "",
    email: data.email || "",
    profilePicture: data.profilePicture || "",
    contactDetails: {
      ...previous.contactDetails,
		firstName: data.name || "",
		lastName: data.lastName || "",
      workRole: data.workRole || "",
      team: data.team || "",
      phone: data.phone || "",
      contactMail: data.email || "",
      isEmailVerified: Boolean(data.validated),
      allowWhatsapp: Boolean(data.allowWhatsapp),
      address: {
        ...previous.contactDetails?.address,
        city: data.city || "",
        country: data.country || "",
        postalCode: data.postalCode || "",
        street: data.address || "",
      },
    },
  };
};

const loadUserProfile = async (setUserProfile, userProfile, setIsLoadingUserPermissions) => {
  try {
    const data = await getProfile();
    const projects = await getProjects();
    const currentProject = selectCurrentProject(projects);
    const projectRole = canonicalRoles.has(currentProject?.role) ? currentProject.role : "guest";
    const projectRoleStatus = currentProject?.roleStatus || "";
    const approvedProjectRole = currentProject && isApproved(projectRoleStatus) ? projectRole : "guest";
    const assignedParcels = approvedProjectRole !== "guest"
      ? await loadCurrentUserParcels(currentProject.id)
      : [];
    const effectivePermissionRole = data.isSuperadmin ? "admin" : approvedProjectRole;
    const profileData = {
      ...mergeProfileResponse(data, userProfile, assignedParcels),
      // `role` remains the legacy UI role. Project membership metadata stays
      // separate so a superadmin is never represented as a Project role.
      role: approvedProjectRole,
      currentProjectId:
        currentProject?.id !== undefined && currentProject?.id !== null ? currentProject.id : null,
      currentProjectName: currentProject?.name || "",
      projectRole,
      projectRoleStatus,
      roleStatus: projectRoleStatus,
      isUserAGuest: false,
      userSettings: {
        darkMode: userProfile.userSettings?.darkMode || "system",
        dashboardLayout: userProfile.userSettings?.dashboardLayout || frontLayouts.simple,
        widgets: userProfile.userSettings?.widgets || widgetData,
        layoutMode: userProfile.userSettings?.layoutMode || "simple",
      },
    };

    setUserProfile((previous) => ({
      ...previous,
      ...profileData,
      contactDetails: { ...previous.contactDetails, ...profileData.contactDetails },
      userSettings: { ...previous.userSettings, ...profileData.userSettings },
    }));
    storeSession(
      {
        accessToken: sessionStorage.getItem("accessToken"),
        accessTokenExpiresAt: sessionStorage.getItem("accessTokenExpiry"),
        userId: data.userId,
      },
      { notifySession: false }
    );
    localStorage.setItem("userProfile", JSON.stringify(profileData));
    localStorage.setItem("contactDetails", JSON.stringify(profileData.contactDetails));
    localStorage.setItem("userSettings", JSON.stringify(profileData.userSettings));
    sessionStorage.setItem(
      "currentProjectId",
      currentProject?.id !== undefined && currentProject?.id !== null ? String(currentProject.id) : ""
    );
    sessionStorage.setItem("currentProjectRole", projectRole);
    sessionStorage.setItem("currentProjectRoleStatus", projectRoleStatus);
    sessionStorage.setItem("frontendPermissions", JSON.stringify(getRolePermissions(effectivePermissionRole)));
    return { ...data, currentProject };
  } finally {
    setIsLoadingUserPermissions(false);
  }
};

export default loadUserProfile;
